import { Link } from "react-router-dom";
import { ShieldCheck, Award, FileCheck2, Wrench, ScrollText, BadgeCheck, ArrowRight } from "lucide-react";
import SeoHead from "../components/SeoHead";
import CTABand from "../components/CTABand";
import { PageHeader } from "./Services";
import { SITE } from "../lib/site";

const ITEMS = [
  {
    icon: BadgeCheck,
    title: "Certibiocide",
    description:
      "Notre équipe est titulaire du Certibiocide, certificat obligatoire pour manipuler les produits biocides destinés au traitement des termites, des insectes xylophages et de la mérule. Ce certificat est délivré par le ministère de la Transition écologique après formation initiale et examen.",
  },
  {
    icon: Award,
    title: "Produits certifiés CTB-P+",
    description:
      "Tous les produits insecticides et fongicides utilisés sur nos chantiers bénéficient du label CTB-P+ délivré par l'Institut Technologique FCBA. Ce label garantit l'efficacité technique, la non-toxicité pour les occupants après séchage et la rémanence prolongée du produit.",
  },
  {
    icon: ScrollText,
    title: "Garantie 5 ans contractuelle",
    description:
      "Tous nos traitements curatifs sont garantis 5 ans. Engagement écrit : si une nouvelle infestation est constatée dans les 5 ans suivant notre intervention, nous revenons traiter à nos frais — sans le moindre euro à votre charge. Cette garantie est inscrite explicitement sur chacun de nos devis et procès-verbaux.",
  },
  {
    icon: ShieldCheck,
    title: "Assurance Responsabilité Civile Professionnelle",
    description:
      "Notre cabinet est couvert par une assurance RC Pro spécifique aux entreprises de traitement antiparasitaire et de protection du bois. Vous êtes intégralement protégés en cas d'incident matériel ou de désordre constaté sur votre habitat.",
  },
  {
    icon: Wrench,
    title: "Aucune sous-traitance",
    description:
      "Tous les chantiers sont réalisés par nos équipes internes. Aucune sous-traitance, aucun intermédiaire. Cela garantit la qualité d'exécution, le respect des protocoles et la traçabilité complète de chaque intervention.",
  },
];

export default function Certifications() {
  return (
    <div data-testid="certifications-page">
      <SeoHead
        title="Certifications & garanties — Termite Bordeaux | Certibiocide, CTB-P+, RC Pro"
        description="Certibiocide, produits CTB-P+, experts certifiés COFRAC, garantie 5 ans, assurance Responsabilité Civile Professionnelle : toutes nos certifications anti-termites en Gironde."
        canonical={SITE.url + "/certifications"}
        keywords={[
          "Certibiocide Bordeaux",
          "CTB-P+ Gironde",
          "certifié termites Bordeaux",
          "garantie 5 ans termites",
          "expert certifié COFRAC",
          "assurance traitement termites",
        ]}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Certifications", url: SITE.url + "/certifications" },
        ]}
        schema={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE.name,
          url: SITE.url,
          telephone: SITE.phoneRaw,
          email: SITE.email,
          hasCredential: ITEMS.map((c) => ({
            "@type": "EducationalOccupationalCredential",
            name: c.title,
            description: c.description,
          })),
        }}
      />
      <PageHeader
        eyebrow="Certifications & garanties"
        title="Pourquoi vous pouvez nous faire confiance."
        intro="Diplômes, certifications, garanties contractuelles, assurances : voici ce qui distingue Termite Bordeaux des prestataires non professionnels."
      />

      <section className="container-x py-16">
        <div className="grid md:grid-cols-2 gap-6">
          {ITEMS.map((it, i) => (
            <article
              key={i}
              className="bg-white border border-[#D5CABD] p-6 rounded-sm"
              data-testid={`cert-item-${i}`}
            >
              <div className="w-12 h-12 grid place-items-center bg-[#7A2E39] text-white rounded-sm mb-4">
                <it.icon className="w-6 h-6" />
              </div>
              <h2 className="font-serif text-2xl text-gray-900 mb-2">{it.title}</h2>
              <p className="text-sm text-gray-700 leading-relaxed">{it.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-[#E8DCC9]/40 border-y border-[#D5CABD]">
        <div className="container-x py-16 max-w-3xl">
          <div className="eyebrow mb-3">Procès-verbal de traitement</div>
          <h2 className="text-3xl font-serif font-semibold text-gray-900 mb-4">
            Un document opposable juridiquement.
          </h2>
          <p className="text-gray-700 mb-4">
            À l'issue de chaque chantier, nous remettons un <strong>procès-verbal de
            traitement</strong> précisant :
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Identité du traitement, date, nature des produits utilisés (nom commercial, n° AMM, dosage)</li>
            <li>Surface traitée et bois concernés (poutres, planchers, soubassements)</li>
            <li>Protocole appliqué (injection, pulvérisation, pièges-appâts, anoxie)</li>
            <li>Durée de garantie contractuelle (5 ans)</li>
            <li>Coordonnées de l'entreprise et signature engageante</li>
          </ul>
          <p className="text-gray-700 mt-4">
            Ce document est <strong>indispensable</strong> en cas de revente du bien, de litige
            avec un acheteur, ou de déclaration à votre assurance habitation.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/services" className="btn-primary" data-testid="cert-services">
              Voir nos protocoles <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/devis" className="btn-ghost">
              Devis gratuit personnalisé
            </Link>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
